(function () {
  /*
   * Custom product total price display
   *
   * Purpose:
   * Update .price_cart #price-field to show:
   * active main product unit price × quantity
   * + selected recommended product price, if recommendation switch is ON.
   *
   * This file is intentionally separate from custom-complementary-add-to-cart.js
   * so it does not risk breaking the free-shipping/recommendation logic.
   */

  const SELECTORS = {
    wrapper: '[data-aid="custom-complementary-products-block"]',
    toggle: '.js-custom-comp-toggle',
    productForm: '#add-to-cart-form, form[action*="/cart/add"]',
    mainVariantPricesJson: '[data-custom-comp-main-variant-prices]',
    moneyContextJson: '[data-custom-comp-money-context]',
    priceField: '.price_cart #price-field'
  };

  let mainVariantPriceMap = null;
  let moneyContext = null;
  let isUpdatingPriceField = false;
  let priceFieldObserverInstalled = false;
  let scheduledUpdateTimeouts = [];
  let cartAddedAt = 0;

  function getProductForm() {
    const priceMap = getMainVariantPriceMap();

    const forms = Array.from(
      document.querySelectorAll(SELECTORS.productForm)
    );

    const matchingMainProductForm = forms.find((form) => {
      const variantInput = form.querySelector('[name="id"]');
      const variantId = variantInput && variantInput.value;

      return variantId && Object.prototype.hasOwnProperty.call(priceMap, variantId);
    });

    if (matchingMainProductForm) {
      return matchingMainProductForm;
    }

    const mainAddButton = document.querySelector(
      '.product-page--submit-action button, .product-page--submit-action input[type="submit"]'
    );

    if (mainAddButton) {
      const mainForm = mainAddButton.closest('form');

      if (mainForm) {
        return mainForm;
      }
    }

    return forms[0] || null;
  }

  function getMainVariantPriceMap() {
    if (mainVariantPriceMap !== null) return mainVariantPriceMap;

    mainVariantPriceMap = {};

    const script = document.querySelector(SELECTORS.mainVariantPricesJson);

    if (!script) return mainVariantPriceMap;

    try {
      mainVariantPriceMap = JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.warn('[custom product total] could not parse variant prices JSON:', error);
      mainVariantPriceMap = {};
    }

    return mainVariantPriceMap;
  }

  function getSelectedMainVariantId() {
    const form = getProductForm();
    const variantInput = form && form.querySelector('[name="id"]');

    return variantInput ? variantInput.value : null;
  }

  function getNormalMainVariantPriceCents() {
    const variantId = getSelectedMainVariantId();
    const priceMap = getMainVariantPriceMap();

    return Number(priceMap[variantId]) || 0;
  }

  function getMainProductQuantity() {
    const form = getProductForm();

    const quantityInput =
      form && form.querySelector('input[name="quantity"], .quantity-selector[name="quantity"]');

    const inputQuantity = Number(quantityInput && quantityInput.value);

    if (Number.isFinite(inputQuantity) && inputQuantity > 0) {
      return inputQuantity;
    }

    const quantitySelect = document.querySelector('select.item-quantity');

    if (quantitySelect) {
      const selectedTextQuantity = Number(
        quantitySelect.selectedOptions &&
        quantitySelect.selectedOptions[0] &&
        quantitySelect.selectedOptions[0].textContent
      );

      if (Number.isFinite(selectedTextQuantity) && selectedTextQuantity > 0) {
        return selectedTextQuantity;
      }

      const selectValue = Number(quantitySelect.value);

      if (Number.isFinite(selectValue) && selectValue >= 0) {
        return selectValue + 1;
      }
    }

    return 1;
  }

  function parseMoneyTextToCents(text) {
    const rawText = String(text || '');

    const numberMatches = rawText.match(/\d[\d\s.,'’]*/g);

    if (!numberMatches || !numberMatches.length) {
      return 0;
    }

    const value = numberMatches[numberMatches.length - 1]
      .trim()
      .replace(/[\s'’]/g, '');
    const context = getMoneyContext();
    const precision = getCurrencyFractionDigits(context.currencyCode);
    const lastComma = value.lastIndexOf(',');
    const lastDot = value.lastIndexOf('.');
    const decimalIndex = Math.max(lastComma, lastDot);
    const digitsAfterSeparator =
      decimalIndex > -1 ? value.length - decimalIndex - 1 : 0;
    const separatorCount = (value.match(/[.,]/g) || []).length;
    const hasDecimalSeparator =
      precision > 0 &&
      decimalIndex > -1 &&
      digitsAfterSeparator > 0 &&
      digitsAfterSeparator <= precision &&
      ((lastComma > -1 && lastDot > -1) || separatorCount === 1);
    let normalized;

    if (hasDecimalSeparator) {
      const fraction = value.slice(decimalIndex + 1).replace(/\D/g, '');

      if (fraction.length > 0) {
        normalized =
          value.slice(0, decimalIndex).replace(/\D/g, '') +
          '.' +
          fraction;
      }
    }

    if (!normalized) {
      normalized = value.replace(/\D/g, '');
    }

    const number = Number.parseFloat(normalized);

    return Number.isFinite(number) ? Math.round(number * 100) : 0;
  }

  function getVisiblePriceText(element) {
    if (!element) {
      return '';
    }

    const clone = element.cloneNode(true);

    clone.querySelectorAll(
      '.visually-hidden, [aria-hidden="true"], [hidden]'
    ).forEach((hiddenElement) => {
      hiddenElement.remove();
    });

    return clone.textContent || '';
  }

  function queryAllDeep(selector, root = document) {
    const results = [];

    if (root.querySelectorAll) {
      results.push(...root.querySelectorAll(selector));
    }

    if (root.querySelectorAll) {
      root.querySelectorAll('*').forEach((element) => {
        if (element.shadowRoot) {
          results.push(...queryAllDeep(selector, element.shadowRoot));
        }
      });
    }

    return results;
  }

  function isSubscriptionPurchaseSelected() {
    const form = getProductForm();
    const sellingPlanInput = form && form.querySelector('[name="selling_plan"]');

    return !!(sellingPlanInput && sellingPlanInput.value);
  }

  function getRechargeSubscriptionPriceCents() {
    if (!isSubscriptionPurchaseSelected()) {
      return 0;
    }

    const priceElements = queryAllDeep(
      '[part="rc-purchase-option__discounted-price"], .rc-price[part*="discounted-price"]'
    );

    const priceElement = priceElements.find((element) => {
      const visibleText = getVisiblePriceText(element);

      return element.getClientRects().length > 0 &&
        parseMoneyTextToCents(visibleText) > 0;
    });

    return priceElement
      ? parseMoneyTextToCents(getVisiblePriceText(priceElement))
      : 0;
  }

  function getMainProductUnitPriceCents() {
    const rechargeSubscriptionPriceCents = getRechargeSubscriptionPriceCents();

    if (rechargeSubscriptionPriceCents) {
      return rechargeSubscriptionPriceCents;
    }

    return getNormalMainVariantPriceCents();
  }

  function getSelectedComplementaryTotalCents() {
    return Array.from(document.querySelectorAll(`${SELECTORS.toggle}:checked`))
      .reduce((total, input) => {
        return total + (Number(input.dataset.variantPrice) || 0);
      }, 0);
  }

  function getDisplayedTotalCents() {
    return (
      getMainProductUnitPriceCents() * getMainProductQuantity()
    ) + getSelectedComplementaryTotalCents();
  }

  function getShopifyCurrencyCode() {
    if (
      window.Shopify &&
      window.Shopify.currency &&
      window.Shopify.currency.active
    ) {
      return window.Shopify.currency.active;
    }

    if (
      window.ShopifyAnalytics &&
      window.ShopifyAnalytics.meta &&
      window.ShopifyAnalytics.meta.currency
    ) {
      return window.ShopifyAnalytics.meta.currency;
    }

    return '';
  }

  function getMoneyContext() {
    if (moneyContext !== null) return moneyContext;

    const script = document.querySelector(SELECTORS.moneyContextJson);
    let parsedContext = {};

    if (script) {
      try {
        parsedContext = JSON.parse(script.textContent || '{}');
      } catch (error) {
        console.warn('[custom product total] could not parse money context JSON:', error);
        parsedContext = {};
      }
    }

    const currencyCode = parsedContext.currencyCode || getShopifyCurrencyCode();
    const locale = parsedContext.locale || document.documentElement.lang || undefined;

    moneyContext = {
      currencyCode: currencyCode ? String(currencyCode).toUpperCase() : '',
      currencySymbol: parsedContext.currencySymbol || '',
      moneySample: parsedContext.moneySample || '',
      locale: locale
    };

    return moneyContext;
  }

  function formatAmountWithDelimiters(cents, precision, thousands, decimal) {
    const normalizedPrecision = precision === null || precision === undefined ? 2 : precision;
    const normalizedThousands = thousands === null || thousands === undefined ? ',' : thousands;
    const normalizedDecimal = decimal || '.';
    const amount = (cents / 100).toFixed(normalizedPrecision);
    const parts = amount.split('.');
    const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, normalizedThousands);
    const centsAmount = parts[1] ? normalizedDecimal + parts[1] : '';

    return dollars + centsAmount;
  }

  function getMoneySampleNumber(sample) {
    const match = String(sample || '').match(/\d[\d\s.,']*\d|\d/);

    return match
      ? {
        text: match[0],
        index: match.index
      }
      : null;
  }

  function getCurrencyFractionDigits(currencyCode) {
    if (!currencyCode) return 2;

    try {
      return new Intl.NumberFormat('en', {
        style: 'currency',
        currency: currencyCode
      }).resolvedOptions().maximumFractionDigits;
    } catch (error) {
      return 2;
    }
  }

  function getMoneySampleSeparators(numberText, currencyCode) {
    const precision = getCurrencyFractionDigits(currencyCode);
    const decimalPattern = precision > 0
      ? new RegExp(`([.,])\\d{${precision}}$`)
      : null;
    const decimalMatch = decimalPattern
      ? String(numberText).match(decimalPattern)
      : null;
    const decimal = decimalMatch ? decimalMatch[1] : '.';
    const integerPart = decimalMatch
      ? numberText.slice(0, -decimalMatch[0].length)
      : numberText;
    const thousandsMatch = integerPart.match(/[^\d]/);

    return {
      decimal: decimal,
      precision: precision,
      thousands: thousandsMatch ? thousandsMatch[0] : ','
    };
  }

  function formatMoneyWithLiquidSample(cents) {
    const context = getMoneyContext();
    const sample = context.moneySample;
    const sampleNumber = getMoneySampleNumber(sample);

    if (!sample || !sampleNumber) {
      return '';
    }

    const separators = getMoneySampleSeparators(
      sampleNumber.text,
      context.currencyCode
    );
    const amount = formatAmountWithDelimiters(
      cents,
      separators.precision,
      separators.thousands,
      separators.decimal
    );

    return amount;
  }

  function formatMoney(cents) {
    const context = getMoneyContext();
    const liquidAmount = formatMoneyWithLiquidSample(cents);

    if (liquidAmount) {
      if (context.currencySymbol) {
        return context.currencySymbol + liquidAmount;
      }

      if (context.currencyCode) {
        return context.currencyCode + ' ' + liquidAmount;
      }

      return liquidAmount;
    }

    if (context.currencySymbol) {
      const amount = formatAmountWithDelimiters(cents, 2, ',', '.');

      return context.currencySymbol + amount;
    }

    if (
      window.Shopify &&
      typeof window.Shopify.formatMoney === 'function'
    ) {
      return window.Shopify.formatMoney(cents);
    }

    return (cents / 100).toFixed(2);
  }

  function setMoneyContent(element, cents) {
    const formattedMoney = formatMoney(cents);
    const context = getMoneyContext();
    const currencyMarker = context.currencySymbol || context.currencyCode;

    element.textContent = '';

    if (
      currencyMarker &&
      formattedMoney.indexOf(currencyMarker) === 0
    ) {
      const symbol = document.createElement('span');

      symbol.className = 'custom-currency-symbol';
      symbol.textContent = currencyMarker;
      symbol.style.setProperty(
        'font-family',
        'Arial, sans-serif',
        'important'
      );

      element.append(
        symbol,
        document.createTextNode(formattedMoney.slice(currencyMarker.length))
      );
      return;
    }

    element.textContent = formattedMoney;
  }

  function normalizeComplementaryProductPrices() {
    document.querySelectorAll(
      `${SELECTORS.wrapper} .complementary-product-card-wrapper`
    ).forEach(function (card) {
      const currentPrice = Number(card.dataset.currentPrice);
      const compareAtPrice = Number(card.dataset.compareAtPrice);
      const currentPriceElement = card.querySelector(
        '.custom-comp-current-price .money'
      );
      const compareAtPriceElement = card.querySelector(
        '.custom-comp-original-price .money'
      );

      if (currentPriceElement && currentPrice > 0) {
        setMoneyContent(currentPriceElement, currentPrice);
      }

      if (compareAtPriceElement && compareAtPrice > 0) {
        setMoneyContent(compareAtPriceElement, compareAtPrice);
      }
    });
  }

  function updateDisplayedTotalPrice() {
    if (cartAddedAt) {
      return;
    }

    const priceField = document.querySelector(SELECTORS.priceField);

    if (!priceField) {
      return;
    }

    const totalCents = getDisplayedTotalCents();

    if (!totalCents) {
      return;
    }

    isUpdatingPriceField = true;

    const moneyElement = document.createElement('span');

    moneyElement.className = 'money';
    setMoneyContent(moneyElement, totalCents);
    priceField.replaceChildren(moneyElement);

    window.setTimeout(function () {
      isUpdatingPriceField = false;
    }, 0);
  }

function scheduleDisplayedTotalPriceUpdate() {
  scheduledUpdateTimeouts.forEach(function(id) { window.clearTimeout(id); });
  scheduledUpdateTimeouts = [];
  [0, 150, 600, 1200, 2000, 3500, 5000].forEach(function(delay) {
    scheduledUpdateTimeouts.push(window.setTimeout(updateDisplayedTotalPrice, delay));
  });
}

  function touchedRechargeWidget(event) {
    const path = typeof event.composedPath === 'function'
      ? event.composedPath()
      : [];

    return path.some((node) => {
      if (!node || !node.classList) {
        return false;
      }

      return Array.from(node.classList).some((className) => {
        return className.indexOf('rc-') === 0;
      });
    });
  }

  function installPriceFieldObserver() {
    if (priceFieldObserverInstalled) {
      return;
    }

    const priceField = document.querySelector(SELECTORS.priceField);

    if (!priceField) {
      return;
    }

    priceFieldObserverInstalled = true;

    const observer = new MutationObserver(function () {
      if (isUpdatingPriceField) {
        return;
      }

      scheduleDisplayedTotalPriceUpdate();
    });

    observer.observe(priceField, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  function installInitialProductTotalReadinessObserver() {
  if (!document.body) {
    return;
  }

  let observerTimer = null;

  const observer = new MutationObserver(function () {
    window.clearTimeout(observerTimer);

    observerTimer = window.setTimeout(function () {
      normalizeComplementaryProductPrices();
      scheduleDisplayedTotalPriceUpdate();
    }, 100);
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  window.setTimeout(function () {
    observer.disconnect();
  }, 8000);
}

function installCartButtonObserver() {
  const button = document.querySelector('#purchase');
  if (!button) return;

  new MutationObserver(function () {
    if (button.disabled || button.querySelector('.lds-dual-ring')) {
      scheduledUpdateTimeouts.forEach(function(id) { window.clearTimeout(id); });
      scheduledUpdateTimeouts = [];
      cartAddedAt = Date.now();
    }
  }).observe(button, {
    attributes: true,
    attributeFilter: ['disabled'],
    childList: true,
    subtree: true
  });
}

function initCustomProductTotalPrice() {
  console.log('[custom product total] loaded');

  installPriceFieldObserver();
  installInitialProductTotalReadinessObserver();
  normalizeComplementaryProductPrices();
  scheduleDisplayedTotalPriceUpdate();

  window.addEventListener('load', function () {
    normalizeComplementaryProductPrices();
    scheduleDisplayedTotalPriceUpdate();
  });

  document.addEventListener('cart:added', function () {
    scheduledUpdateTimeouts.forEach(function(id) { window.clearTimeout(id); });
    scheduledUpdateTimeouts = [];
    cartAddedAt = Date.now();
  });

  installCartButtonObserver();

    document.addEventListener('change', function (event) {
      if (!event.isTrusted) return;
      const target = event.target;

      if (
        target.matches(SELECTORS.toggle) ||
        target.matches('[name="id"]') ||
        target.matches('[name="quantity"]') ||
        target.matches('.quantity-selector') ||
        target.matches('.item-quantity') ||
        target.matches('[name="selling_plan"]') ||
        target.closest(SELECTORS.productForm)
      ) {
        cartAddedAt = 0;
        scheduleDisplayedTotalPriceUpdate();
      }
    });

    document.addEventListener('input', function (event) {
      if (!event.isTrusted) return;
      const target = event.target;

      if (
        target.matches('[name="quantity"]') ||
        target.matches('.quantity-selector') ||
        target.matches('.item-quantity')
      ) {
        cartAddedAt = 0;
        scheduleDisplayedTotalPriceUpdate();
      }
    });

    document.addEventListener('click', function (event) {
      if (!event.isTrusted) return;
      if (
        event.target.closest('.qty-plus, .qty-minus') ||
        event.target.closest(SELECTORS.toggle) ||
        touchedRechargeWidget(event)
      ) {
        cartAddedAt = 0;
        scheduleDisplayedTotalPriceUpdate();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomProductTotalPrice);
  } else {
    initCustomProductTotalPrice();
  }
})();
